import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from "@angular/core";
import { RouterModule, ActivatedRoute } from "@angular/router";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { rxResource, toSignal } from "@angular/core/rxjs-interop";
import { ApiService } from "../core/api.service";
import { CustomerSummary, Estimate } from "../core/models";
import {
  Observable,
  catchError,
  combineLatest,
  forkJoin,
  finalize,
  map,
  of,
  switchMap,
} from "rxjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

@Component({
  selector: "app-customers-page",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterModule, MatProgressBarModule],
  templateUrl: "./customers-page.component.html",
  styleUrls: ["./customers-page.component.scss"],
})
export class CustomersPageComponent {
  private readonly api = inject(ApiService);
  private readonly route = inject(ActivatedRoute);

  readonly todayLabel: string;

  readonly downloading = signal(false);
  readonly message = signal("");

  readonly search = signal(
    (this.route.snapshot.queryParamMap.get("q") || "").trim(),
  );
  readonly pageSize = 10;
  readonly page = signal(0);

  private readonly customersResource = rxResource({
    stream: () =>
      this.api.getCustomerSummaries().pipe(
        catchError((err) => {
          console.error("Failed to load customers:", err);
          this.message.set("Failed to load customers.");
          return of([] as CustomerSummary[]);
        }),
      ),
  });

  readonly loading = this.customersResource.isLoading;

  private readonly filteredCustomers = computed(() => {
    const normalized = this.search().trim().toLowerCase();
    const customers = this.customersResource.value() ?? [];
    return normalized
      ? customers.filter((c) =>
          this.buildSearchText(c).includes(normalized),
        )
      : customers;
  });

  readonly projectStats = computed(() =>
    this.buildStats(this.filteredCustomers().filter((c) => !this.isArchived(c))),
  );

  readonly activeTab = signal<JobTab>("in-progress");
  readonly restoringId = signal<string | null>(null);

  readonly tabs: { value: JobTab; label: string }[] = [
    { value: "in-progress", label: "In Progress" },
    { value: "warranty", label: "Under 6-Month Warranty" },
    { value: "old", label: "Old Orders" },
    { value: "archived", label: "Archived" },
  ];

  private isInstalled(c: CustomerSummary): boolean {
    return c.status === "Installed" || c.status === "Complete paid";
  }

  private isArchived(c: CustomerSummary): boolean {
    return !!c.archivedAt;
  }

  private isWithinWarranty(c: CustomerSummary): boolean {
    if (!this.isInstalled(c) || !c.installedAt) return false;
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 6);
    return new Date(c.installedAt) >= cutoff;
  }

  readonly projectCards = computed(() => {
    const tab = this.activeTab();
    return this.buildProjectCards(
      this.filteredCustomers().filter((c) => {
        if (tab === "archived") return this.isArchived(c);
        if (this.isArchived(c)) return false;
        if (tab === "in-progress") return !this.isInstalled(c);
        if (tab === "warranty") return this.isWithinWarranty(c);
        return this.isInstalled(c) && !this.isWithinWarranty(c);
      }),
    );
  });

  readonly pageCount = computed(() =>
    Math.max(1, Math.ceil(this.projectCards().length / this.pageSize)),
  );

  readonly pageRows = computed(() => {
    const page = Math.min(this.page(), this.pageCount() - 1);
    const start = page * this.pageSize;
    return this.projectCards().slice(start, start + this.pageSize);
  });

  setSearch(value: string) {
    this.search.set(value);
    this.page.set(0);
  }

  setPage(delta: number) {
    this.page.set(
      Math.max(0, Math.min(this.page() + delta, this.pageCount() - 1)),
    );
  }

  selectTab(tab: JobTab) {
    this.activeTab.set(tab);
    this.page.set(0);
  }

  restore(customerId: string) {
    if (this.restoringId()) return;
    this.restoringId.set(customerId);
    this.api.restoreCustomer(customerId).subscribe({
      next: () => {
        this.restoringId.set(null);
        this.customersResource.reload();
        this.message.set("Job restored.");
      },
      error: (err) => {
        this.restoringId.set(null);
        this.message.set(
          err?.status === 404
            ? "Restore isn't available yet."
            : "Failed to restore job.",
        );
      },
    });
  }

  constructor() {
    this.todayLabel = new Date().toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
    });
  }

  private buildSearchText(customer: CustomerSummary): string {
    return [
      customer.name,
      customer.email,
      customer.phone,
      customer.address,
      customer.builder,
      customer.model,
      customer.elevation,
      customer.status,
    ]
      .filter(
        (value): value is string =>
          typeof value === "string" && value.trim().length > 0,
      )
      .join(" ")
      .toLowerCase();
  }

  private buildStats(customers: CustomerSummary[]): ProjectStats {
    const total = customers.length;
    const complete = customers.filter((c) => this.isInstalled(c)).length;
    const inProgress = customers.filter(
      (c) => !this.isInstalled(c) && c.roomsCount > 0,
    ).length;

    return {
      inProgress,
      complete,
      total,
    };
  }

  private buildProjectCards(customers: CustomerSummary[]): ProjectCard[] {
    const palettes = [
      "var(--accent-peach)",
      "var(--accent-lavender)",
      "var(--accent-mint)",
      "var(--accent-blue)",
      "var(--accent-sand)",
      "var(--accent-rose)",
      "var(--accent-lemon)",
      "var(--accent-sage)",
      "var(--accent-sky)",
      "var(--accent-lilac)",
      "#ffe6f0",
      "#dff5ff",
    ];

    return customers.map((customer, index) => {
      const progress = this.calculateProgress(customer);
      return {
        id: customer.id,
        title: customer.name,
        dateLabel: this.formatDate(customer.createdAt),
        measurementDateLabel: customer.lastMeasurementAt
          ? this.formatDate(customer.lastMeasurementAt)
          : undefined,
        progress,
        color: palettes[index % palettes.length],
        roomsCount: customer.roomsCount,
        windowsCount: customer.windowsCount,
        status: customer.status,
        archivedAt: customer.archivedAt,
        isRemake: !!customer.remakeOfCustomerId,
      };
    });
  }

  private calculateProgress(customer: CustomerSummary): number {
    const statusProgressMap: Record<string, number> = {
      Onboarded: 10,
      Measured: 25,
      "Estimate given": 40,
      "Advance paid": 55,
      "In production": 70,
      Delivered: 85,
      Installed: 95,
      "Complete paid": 100,
    };

    if (customer.status && statusProgressMap[customer.status]) {
      return statusProgressMap[customer.status];
    }

    // If no status, return 0
    return 0;
  }

  private formatDate(value: string): string {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return "";
    }
    return parsed.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }

  downloadBlindsReport(customerId: string) {
    if (this.downloading()) return;
    this.downloading.set(true);

    this.getCustomerWithRoomsAndWindows(customerId)
      .pipe(
        switchMap(({ customer, roomsWithWindows }) => {
          const allWindows = roomsWithWindows.flatMap((rw) => rw.windows);
          const windowIds = allWindows.map((w) => w.id);
          const measurements$ = windowIds.length
            ? this.api
                .getMeasurementsBulk(windowIds)
                .pipe(catchError(() => of([] as any[])))
            : of([] as any[]);
          return measurements$.pipe(
            map((allMeasurements) => {
              const measurementsByWindow = new Map<string, any[]>();
              for (const m of allMeasurements) {
                const bucket = measurementsByWindow.get(m.windowId) ?? [];
                bucket.push(m);
                measurementsByWindow.set(m.windowId, bucket);
              }
              const enriched = roomsWithWindows.map((rw) => ({
                room: rw.room,
                windows: rw.windows.map((w: any) => ({
                  ...w,
                  measurements: measurementsByWindow.get(w.id) ?? [],
                })),
              }));
              return { customer, roomsWithWindows: enriched };
            }),
          );
        }),
        catchError(() => {
          this.message.set("Failed to download report.");
          return of(null);
        }),
        finalize(() => this.downloading.set(false)),
      )
      .subscribe((result) => {
        if (!result) return;
        const { customer, roomsWithWindows } = result;
        const csv = this.generateBlindsReportCSV(customer, roomsWithWindows);
        this.downloadCSV(csv, `${customer.name}_blinds_report.csv`);
      });
  }

  downloadEstimatePDF(customerId: string) {
    if (this.downloading()) return;
    this.downloading.set(true);

    this.api.getCustomer(customerId)
      .pipe(
        // try the saved estimate first; fall back to generating one from data
        switchMap((customer) =>
          this.api.getEstimate(customerId).pipe(
            map((estimate) => ({ customer, estimate })),
            catchError(() => this.buildEstimateFromData(customerId, customer)),
          ),
        ),
        catchError(() => {
          this.message.set("Failed to download estimate.");
          return of(null);
        }),
        finalize(() => this.downloading.set(false)),
      )
      .subscribe((result) => {
        if (!result) return;
        this.generateEstimatePDF(result.customer, result.estimate);
      });
  }

  /** Loads customer + rooms + windows (single aggregate windows request). */
  private getCustomerWithRoomsAndWindows(customerId: string) {
    return combineLatest({
      customer: this.api.getCustomer(customerId),
      rooms: this.api.getRooms(customerId),
    }).pipe(
      switchMap(({ customer, rooms }) =>
        this.loadRoomsWithWindows(customerId, rooms).pipe(
          map((roomsWithWindows) => ({ customer, roomsWithWindows })),
        ),
      ),
    );
  }

  /**
   * Fetches all windows for a customer in ONE request, then groups them by
   * room. Previously this fired N parallel per-room requests, which tripped
   * Lambda throttling (503) and silently dropped rooms.
   */
  private loadRoomsWithWindows(
    customerId: string,
    rooms: RoomLike[],
  ): Observable<RoomWithWindows[]> {
    if (!rooms.length) {
      return of([] as RoomWithWindows[]);
    }
    return this.api.getCustomerWindows(customerId).pipe(
      catchError(() => of([] as any[])),
      map((windows) =>
        rooms.map((room) => ({
          room,
          windows: windows.filter((w) => w.roomId === room.id),
        })),
      ),
    );
  }

  /** Builds an estimate from rooms/windows when no saved estimate exists. */
  private buildEstimateFromData(
    customerId: string,
    customer: any,
  ): Observable<{ customer: any; estimate: Estimate }> {
    return this.api.getRooms(customerId).pipe(
      switchMap((rooms) =>
        this.loadRoomsWithWindows(customerId, rooms).pipe(
          switchMap((roomsWithWindows) => {
            const allWindows = roomsWithWindows.flatMap((rw) => rw.windows);
            const windowIds = allWindows.map((w) => w.id);
            const measurements$ = windowIds.length
              ? this.api
                  .getMeasurementsBulk(windowIds)
                  .pipe(catchError(() => of([] as any[])))
              : of([] as any[]);
            return measurements$.pipe(
              map((allMeasurements) => {
                const latestByWindow = new Map<string, any>();
                for (const m of allMeasurements) {
                  const cur = latestByWindow.get(m.windowId);
                  if (!cur || (m.createdAt || "") > (cur.createdAt || "")) {
                    latestByWindow.set(m.windowId, m);
                  }
                }
                const sortedRooms = [...roomsWithWindows].sort((a, b) =>
                  a.room.name.localeCompare(b.room.name, undefined, {
                    sensitivity: "base",
                  }),
                );
                const items = sortedRooms.flatMap(({ room, windows }) => {
                  const sortedWindows = [...windows].sort((a: any, b: any) =>
                    (a.label ?? "").localeCompare(b.label ?? "", undefined, {
                      sensitivity: "base",
                    }),
                  );
                  return sortedWindows.map((w: any) => {
                    const measurement = latestByWindow.get(w.id);
                    const widthIn = measurement
                      ? measurement.unit === "cm"
                        ? measurement.width / 2.54
                        : measurement.width
                      : undefined;
                    const baseRate = this.baseBlindPrice(w, widthIn);
                    const motorizedSurcharge =
                      w.controlType === "motorized" ? 70 : 0;
                    const solarSurcharge = w.hasSolarPanel ? 40 : 0;
                    const rate =
                      baseRate + motorizedSurcharge + solarSurcharge;
                    return {
                      roomName: room.name,
                      windowLabel: w.label ?? "",
                      quantity: 1,
                      rate,
                      discount: 0,
                      discountPercent: 0,
                      amount: rate,
                      style: w.style,
                      controlType: w.controlType,
                      hasSolarPanel: w.hasSolarPanel,
                    };
                  });
                });
                const subtotal = items.reduce((s, i) => s + i.amount, 0);
                const estimate: Estimate = {
                  id: "DRAFT",
                  customerId,
                  items,
                  windowIds,
                  subtotal,
                  totalDiscount: 0,
                  total: subtotal,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                };
                return { customer, estimate };
              }),
            );
          }),
        ),
      ),
    );
  }

  /** Base blind price depends on the measured width tier. */
  private baseBlindPrice(window: any, widthIn?: number): number {
    if (window.price && window.price > 0) {
      return window.price;
    }
    if (widthIn !== undefined && widthIn < 20) {
      return 170;
    }
    if (widthIn !== undefined && widthIn > 50) {
      return 300;
    }
    return 250;
  }

  private generateEstimatePDF(customer: any, estimate: Estimate): void {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });
    const margin = 14;

    let y = 16;
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(customer.name, margin, y);
    y += 6;
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(`Estimate: ${estimate.id}`, margin, y);
    y += 10;

    const tableBody = [...estimate.items]
      .sort((a, b) => {
        const roomCmp = a.roomName.localeCompare(b.roomName, undefined, {
          sensitivity: "base",
        });
        if (roomCmp !== 0) return roomCmp;
        return (a.windowLabel ?? "").localeCompare(
          b.windowLabel ?? "",
          undefined,
          { sensitivity: "base" },
        );
      })
      .map((item) => [
        item.roomName,
        item.windowLabel,
        String(item.quantity),
        `$${item.rate.toFixed(2)}`,
        item.discount > 0 ? `$${item.discount.toFixed(2)}` : "",
        `$${item.amount.toFixed(2)}`,
      ]);

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: [["Room", "Position", "Qty", "Rate", "Discount", "Amount"]],
      body: tableBody,
      theme: "plain",
      styles: {
        fontSize: 9,
        cellPadding: 3,
        textColor: [0, 0, 0],
        lineColor: [180, 180, 180],
        lineWidth: 0.1,
      },
      headStyles: {
        fontStyle: "bold",
        fillColor: false,
        textColor: [0, 0, 0],
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
      },
      alternateRowStyles: {},
      columnStyles: {
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
      },
    });

    y = (doc as any).lastAutoTable.finalY + 6;
    const pageW = doc.internal.pageSize.getWidth();
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);

    const totals: [string, string][] = [
      ["Subtotal", `$${estimate.subtotal.toFixed(2)}`],
    ];
    if (estimate.totalDiscount > 0) {
      totals.push(["Discount", `-$${estimate.totalDiscount.toFixed(2)}`]);
    }
    totals.push(["Total", `$${estimate.total.toFixed(2)}`]);

    totals.forEach(([label, value], i) => {
      const isLast = i === totals.length - 1;
      if (isLast) {
        doc.setFont("helvetica", "bold");
      }
      doc.text(label, pageW - margin - 60, y);
      doc.text(value, pageW - margin, y, { align: "right" });
      y += 6;
    });

    doc.save(`${customer.name.replace(/\s+/g, "_")}_estimate.pdf`);
  }

  private generateBlindsReportCSV(
    customer: any,
    roomsWithWindows: RoomWithWindows[],
  ): string {
    const headers = [
      "Room name",
      "Position",
      "Width",
      "Height",
      "Motorized",
      "Count",
      "Needs Solar",
      "Style",
    ];

    const rows: string[] = [headers.join(",")];

    const sortedRooms = [...roomsWithWindows].sort((a, b) =>
      a.room.name.localeCompare(b.room.name, undefined, {
        sensitivity: "base",
      }),
    );

    sortedRooms.forEach(({ room, windows }, roomIndex) => {
      const sortedWindows = [...windows].sort((a: any, b: any) =>
        (a.label ?? "").localeCompare(b.label ?? "", undefined, {
          sensitivity: "base",
        }),
      );
      sortedWindows.forEach((window: any) => {
        const motorized = window.controlType === "motorized" ? "✓" : "";
        const solar = window.hasSolarPanel ? "✓" : "";

        const sorted = [...(window.measurements ?? [])].sort((a: any, b: any) =>
          (b.createdAt || "").localeCompare(a.createdAt || ""),
        );
        const measurement = sorted[0] ?? null;
        const width = measurement?.width ?? "";
        const height = measurement?.height ?? "";

        const row = [
          room.name,
          window.label,
          width,
          height,
          motorized,
          "1",
          solar,
          window.style ?? "",
        ];
        rows.push(`"${row.join('","')}"`);
      });

      // Empty separator row between rooms (skip after last room)
      if (roomIndex < sortedRooms.length - 1) {
        rows.push("");
      }
    });

    const totalBlinds = sortedRooms.reduce(
      (sum, { windows }) => sum + windows.length,
      0,
    );
    rows.push("");
    rows.push(`"Total blinds","${totalBlinds}"`);

    return rows.join("\n");
  }

  private downloadCSV(csv: string, filename: string) {
    const element = document.createElement("a");
    element.setAttribute(
      "href",
      "data:text/csv;charset=utf-8," + encodeURIComponent(csv),
    );
    element.setAttribute("download", filename);
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  }
}

interface RoomLike {
  id: string;
  name: string;
}

interface RoomWithWindows {
  room: RoomLike;
  windows: any[];
}

type JobTab = "in-progress" | "warranty" | "old" | "archived";

interface ProjectCard {
  id: string;
  title: string;
  dateLabel: string;
  measurementDateLabel?: string;
  progress: number;
  color: string;
  roomsCount: number;
  windowsCount: number;
  status?: string;
  archivedAt?: string;
  isRemake: boolean;
}

interface ProjectStats {
  inProgress: number;
  complete: number;
  total: number;
}
